import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, CheckCircle2, Clock, XCircle, Wallet, Percent } from "lucide-react";
import { PageHeader, Spinner, Card, EmptyState } from "@/components/dash";
import StatusBadge from "@/components/StatusBadge";
import CoinIcon from "@/components/CoinIcon";
import api from "@/lib/api";
import { formatTRY, formatCrypto, formatDate } from "@/lib/format";

function Stat({ icon: Icon, label, value, tone = "primary" }) {
  const tones = { primary: "text-primary bg-primary/10", success: "text-emerald-500 bg-emerald-500/10", warning: "text-amber-500 bg-amber-500/10", danger: "text-red-500 bg-red-500/10" };
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon size={18} strokeWidth={1.5} />
        </span>
      </div>
      <p className="mt-4 text-2xl font-extrabold tracking-tighter">{value}</p>
      <p className="mt-0.5 text-sm text-muted-foreground">{label}</p>
    </Card>
  );
}

export default function Overview() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => setStats({}));
  }, []);

  if (!stats) return <Spinner />;

  return (
    <div>
      <PageHeader title="Genel Bakış" desc="İşletmenizin ödeme performansını izleyin." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={TrendingUp} label="Toplam Satış" value={formatTRY(stats.total_sales || 0)} tone="primary" />
        <Stat icon={CheckCircle2} label="Başarılı Ödemeler" value={stats.confirmed_count || 0} tone="success" />
        <Stat icon={Clock} label="Bekleyen Ödemeler" value={stats.pending_count || 0} tone="warning" />
        <Stat icon={XCircle} label="Başarısız Ödemeler" value={stats.failed_count || 0} tone="danger" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Stat icon={Wallet} label="Kullanılabilir Bakiye" value={formatTRY(stats.available_try || 0)} tone="primary" />
        <Stat icon={Percent} label="Toplam Komisyon (kripto)" value={(stats.total_commission || 0).toFixed(6)} tone="warning" />
      </div>

      <Card className="mt-6 p-6">
        <h3 className="text-base font-bold mb-4">Günlük Ödeme Hacmi</h3>
        {stats.chart && stats.chart.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={stats.chart} margin={{ left: -10, right: 10 }}>
              <defs>
                <linearGradient id="vol" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(216 100% 50%)" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(216 100% 50%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
              <Tooltip formatter={(v) => formatTRY(v)} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
              <Area type="monotone" dataKey="volume" stroke="hsl(216 100% 50%)" strokeWidth={2.5} fill="url(#vol)" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="py-12 text-center text-sm text-muted-foreground">Henüz veri yok. İlk ödemenizi oluşturun.</div>
        )}
      </Card>

      <Card className="mt-6 p-6">
        <h3 className="text-base font-bold mb-4">Son İşlemler</h3>
        {stats.recent && stats.recent.length > 0 ? (
          <div className="space-y-2">
            {stats.recent.map((p) => (
              <div key={p.id} className="flex items-center gap-3 rounded-xl border border-border p-3">
                {p.coin ? <CoinIcon symbol={p.coin} size={30} /> : <div className="h-[30px] w-[30px] rounded-full bg-muted" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{formatTRY(p.amount_try)}</p>
                  <p className="text-xs text-muted-foreground truncate">{p.order_id} · {formatDate(p.created_at)}</p>
                </div>
                <StatusBadge status={p.status} />
              </div>
            ))}
          </div>
        ) : (
          <EmptyState title="Henüz işlem yok" desc="İlk ödemenizi oluşturduğunuzda burada görünecek." />
        )}
      </Card>
    </div>
  );
}
